import React, { useState } from "react";
import classes from "./ApproveLoan.module.css"; // Update naming conventions where needed
import Swal from "sweetalert2";
import api from "../../Components/API/Api";
import { Button, Form, Modal } from "react-bootstrap";

/* ─── SVG Icons ──────────────────────────────────────────────────── */
const Icon = {
  Document: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  User: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Bank: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="10" width="18" height="11" rx="1"/>
      <path d="M12 2L2 7h20L12 2z"/>
      <line x1="7" y1="10" x2="7" y2="21"/>
      <line x1="12" y1="10" x2="12" y2="21"/>
      <line x1="17" y1="10" x2="17" y2="21"/>
    </svg>
  ),
  Shield: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <polyline points="9 12 11 14 15 10"/>
    </svg>
  ),
  Wallet: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
      <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
      <path d="M18 12a2 2 0 1 0 4 0 2 2 0 0 0-4 0z"/>
    </svg>
  ),
  Note: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  ),
  CheckCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="9 12 11 14 15 10"/>
    </svg>
  ),
  XCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="15" y1="9" x2="9" y2="15"/>
      <line x1="9" y1="9" x2="15" y2="15"/>
    </svg>
  ),
  Circle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <circle cx="12" cy="12" r="9"/>
    </svg>
  ),
};

/* ─── Helpers ────────────────────────────────────────────────────── */
const fmt   = (v) => (v !== undefined && v !== null && v !== "" ? v : "—");
const money = (v) => v ? `₦${Number(v).toLocaleString("en-NG", { minimumFractionDigits: 2 })}` : "—";

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-NG", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  return d.toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" });
};

/* ─── Info Row ───────────────────────────────────────────────────── */
const InfoRow = ({ label, value, badge }) => (
  <div className={classes.infoRow}>
    <span className={classes.infoRowIcon}><Icon.Circle /></span>
    <span className={classes.infoRowLabel}>{label}</span>
    <span className={classes.infoRowColon}>:</span>
    {badge !== undefined ? (
      <span className={
        badge === "Yes" || badge === "Active" || badge === "Approved"
          ? classes.badgeGreen
          : badge === "Disapproved"
          ? classes.badgeRed
          : badge === "Pending"
          ? classes.badgeAmber
          : classes.badgeRed
      }>
        {badge}
      </span>
    ) : (
      <span className={classes.infoRowValue}>{value ?? "—"}</span>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
    COMPONENT (MODAL)
═══════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════
    COMPONENT (MODAL)
═══════════════════════════════════════════════════════════════════ */
const ApproveLoanModal = ({ isOpen,
    onClose,
    loanData,
    approvalType = "all",
    onSuccess }) => {
  // 1. Initialize ALL Hooks at the very top level
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [comment, setComment] = useState("");
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");

  // 2. NOW you can handle the early return if the modal shouldn't render
  if (!isOpen) return null;

  const d = loanData || {};
  const member = d.member || {};
  const loan   = d.loan   || {};
  const bank   = d.bank   || {};

  const statusLabel =
    Number(d.approved) === 1 ? "Approved" :
    Number(d.approved) === 2 ? "Disapproved" : "Pending";

  const approvedLabel  = Number(d.approved)  === 1 ? "Yes" : "No";
  const disbursedLabel = Number(d.disbursed) === 1 ? "Yes" : "No";

  const openFile = (file) => {
    if (!file) {
      Swal.fire({
        icon: "warning",
        title: "No File",
        text: "No attachment was uploaded for this loan."
      });
      return;
    }
    setSelectedFile(file);
    setShowFileModal(true);
  };

  const openActionInputModal = (type) => {
    setActionType(type);
    setComment("");
    setShowModal(true);
  };

  const handleAction = async () => {
    if (!comment.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Comment Required",
        text: "Please enter your comment before proceeding.",
        target: `.${classes.innerModalOverlay}` 
      });
      return;
    }

    setSubmitting(true);

    try {
      const companyId = localStorage.getItem("company_id") || localStorage.getItem("companyId");
      const endpoint =
    approvalType === "additional"
        ? actionType === "approve"
            ? "/v1/approve_additional_loan"
            : "/v1/disapprove_additional_loan"
        : actionType === "approve"
            ? "/v1/approve_loan"
            : "/v1/disapprove_loan";

      const response = await api.post(endpoint, {
        company_id: companyId,
        loan_id: d.id,
        comment: comment.trim()
      });

      if (response.data.status) {
        setShowModal(false);

        await Swal.fire({
          icon: "success",
          title:
    approvalType === "additional"
        ? actionType === "approve"
            ? "Additional Loan Approved"
            : "Additional Loan Disapproved"
        : actionType === "approve"
            ? "Loan Approved"
            : "Loan Disapproved",
          text: response.data.message,
          timer: 1500,
          showConfirmButton: false
        });

        if (onSuccess) onSuccess();
        onClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: response.data.message || "Action could not be completed.",
          target: `.${classes.innerModalOverlay}`
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Something went wrong.",
        target: `.${classes.innerModalOverlay}`
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Main Modal Backdrop Layer */}
     <Modal
    show={isOpen}
    onHide={onClose}
    size="xl"
    centered
    backdrop="static"
    keyboard={false}
    scrollable
>
    <Modal.Header closeButton>
        <Modal.Title>{approvalType === "additional"
        ? "Review Additional Loan Request"
        : "Review Loan Application"}</Modal.Title>
    </Modal.Header>

    <Modal.Body>
          
          {/* Modal Header containing close trigger */}
          {/* <div className={classes.modalHeaderRow}>
            <h3>Review Loan Application</h3>
            <button className={classes.modalCloseX} onClick={onClose}>✕</button>
          </div> */}

          <div className={classes.pageWrapper}>
            {/* <div className={classes.hero}>
              <div className={classes.heroLeft}>
                <div className={classes.heroIconWrap}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <polyline points="9 12 11 14 15 10"/>
                  </svg>
                </div>
                <div>
                  <div className={classes.heroTitle}>Loan Approval</div>
                  <div className={classes.heroSub}>Review loan application details and take action</div>
                </div>
              </div>

              <div className={classes.statusCard}>
                <div className={classes.statusCardLabel}>Application Status</div>
                <div className={
                  statusLabel === "Approved"    ? classes.statusBadgeGreen  :
                  statusLabel === "Disapproved" ? classes.statusBadgeRed    :
                  classes.statusBadgeAmber
                }>
                  {statusLabel.toUpperCase()}
                </div>
              </div>
            </div> */}

            {/* ── SUMMARY STRIP ────────────────────────────────────────── */}
            <div className={classes.summaryStrip}>
              <div className={classes.summaryItem}>
                <div className={`${classes.summaryIconWrap} ${classes.blue}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.58-7 8-7s8 3 8 7"/>
                  </svg>
                </div>
                <div className={classes.summaryMeta}>
                  <div className={classes.summaryCaption}>Member</div>
                  <div className={classes.summaryValue}>{fmt(member.name)}</div>
                  <div className={classes.summarySub}>Member No: {fmt(member.member_number)}</div>
                </div>
              </div>

              <div className={classes.summaryItem}>
                <div className={`${classes.summaryIconWrap} ${classes.green}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="10" width="18" height="11" rx="1"/><path d="M12 2L2 7h20L12 2z"/>
                    <line x1="7" y1="10" x2="7" y2="21"/><line x1="12" y1="10" x2="12" y2="21"/><line x1="17" y1="10" x2="17" y2="21"/>
                  </svg>
                </div>
                <div className={classes.summaryMeta}>
                  <div className={classes.summaryCaption}>Loan Type</div>
                  <div className={classes.summaryValue}>{fmt(loan.loan_name)}</div>
                  <div className={classes.summarySub}>{fmt(loan.loan_category)}</div>
                </div>
              </div>

              <div className={classes.summaryItem}>
                <div className={`${classes.summaryIconWrap} ${classes.purple}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v12M9 9h4.5a1.5 1.5 0 0 1 0 3H9.5a1.5 1.5 0 0 0 0 3H15"/>
                  </svg>
                </div>
                <div className={classes.summaryMeta}>
                  <div className={classes.summaryCaption}>Principal Amount</div>
                  <div className={classes.summaryValue}>{money(d.principal_amount)}</div>
                  <div className={classes.summarySub}>Total Loan</div>
                </div>
              </div>

              <div className={classes.summaryItem}>
                <div className={`${classes.summaryIconWrap} ${classes.orange}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <div className={classes.summaryMeta}>
                  <div className={classes.summaryCaption}>Transaction Date</div>
                  <div className={classes.summaryValue}>{formatDate(d.transaction_date)}</div>
                  <div className={classes.summarySub}>Due in {fmt(d.duration)} months</div>
                </div>
              </div>
            </div>

            {/* ── CONTENT ──────────────────────────────────────────────── */}
            <div className={classes.content}>
              <div className={classes.sectionCard}>
                <div className={classes.sectionCardHeader}>
                  <Icon.Document />
                  <h3>Loan Details</h3>
                </div>
                <div className={classes.sectionCardBody}>
                  <div className={classes.threeCol}>
                    <div className={classes.infoCol}>
                      <div className={`${classes.infoColTitle} ${classes.blue}`}>
                        <Icon.Document /> Application Information
                      </div>
                      <InfoRow label="Application Prefix" value={fmt(d.prefix)} />
                      <InfoRow label="Application ID"     value={fmt(d.id)} />
                      <InfoRow label="Application Date"   value={formatDate(d.transaction_date)} />
                      <InfoRow label="Created By"         value={fmt(d.created_by?.name)} />
                      <InfoRow label="Status"             badge={statusLabel} />
                    </div>

                    <div className={classes.infoCol}>
                      <div className={`${classes.infoColTitle} ${classes.green}`}>
                        <Icon.User /> Member Information
                      </div>
                      <InfoRow label="Name"          value={fmt(member.name)} />
                      <InfoRow label="Member Number" value={fmt(member.member_number)} />
                      <InfoRow label="Title"         value={fmt(member.title)} />
                      <InfoRow label="Employee ID"   value={fmt(d.employee_id)} />
                      <InfoRow label="Company ID"    value={fmt(d.company_id)} />

                      <div className={classes.monthlyCard}>
                        <div className={classes.monthlyCardIcon}><Icon.Wallet /></div>
                        <div>
                          <div className={classes.monthlyCardLabel}>Monthly Deduction</div>
                          <div className={classes.monthlyCardValue}>{money(d.monthly_deduction)}</div>
                        </div>
                      </div>
                    </div>

                    <div className={classes.infoCol} style={{ paddingRight: 0 }}>
                      <div className={`${classes.infoColTitle} ${classes.purple}`}>
                        <Icon.Document /> Loan Information
                      </div>
                      <InfoRow label="Loan Code"         value={fmt(loan.loan_code)} />
                      <InfoRow label="Loan Name"         value={fmt(loan.loan_name)} />
                      <InfoRow label="Loan Category"     value={fmt(loan.loan_category)} />
                      <InfoRow label="Duration"          value={d.duration ? `${d.duration} months` : "—"} />
                      <InfoRow label="Principal Amount"  value={money(d.principal_amount)} />
                      <InfoRow label="Interest Amount"   value={money(d.interest_amount)} />
                      <InfoRow label="Loan Interest"     value={money(d.loan_interest)} />
                      <InfoRow label="Total Loan"        value={money(d.total_loan)} />
                      <InfoRow label="Total Repayment"   value={money(d.total_repayment)} />
                      <InfoRow label="Monthly Deduction" value={money(d.monthly_deduction)} />
                      <InfoRow label="Balance"           value={money(d.balance)} />
                    </div>
                  </div>
                </div>
              </div>

              <div className={classes.twoColRow}>
                <div className={classes.sectionCard}>
                  <div className={classes.sectionCardHeader}>
                    <Icon.Bank />
                    <h3>Financial &amp; Disbursement Details</h3>
                  </div>
                  <div className={classes.sectionCardBody}>
                    <InfoRow label="Bank"              value={fmt(bank.gl_name)} />
                    <InfoRow label="Bank Code"         value={fmt(d.bank?.gl_code)} />
                    <InfoRow label="Disbursed"         badge={disbursedLabel} />
                    <InfoRow label="Disbursed Amount"  value={money(d.disbursed_amount)} />
                    <InfoRow label="Disbursed By"      value={fmt(d.disbursed_by?.name)} />
                    <InfoRow label="Reference Number"  value={fmt(d.reference_number)} />
                    <InfoRow label="Transaction Date"  value={formatDate(d.transaction_date)} />
                  </div>
                </div>

                <div className={classes.sectionCard}>
                  <div className={classes.sectionCardHeader}>
                    <Icon.Shield />
                    <h3>Approval Information</h3>
                  </div>
                  <div className={classes.sectionCardBody}>
                    <InfoRow label="Approved" badge={approvedLabel} />
                    {(Number(d.approved) === 1 || Number(d.approved) === 0) && (
                      <>
                        <InfoRow label="Approved By" value={fmt(d.approval_comment?.[0]?.approver_name)} />
                        <InfoRow label="Approved Date" value={fmt(d.approval_comment?.[0]?.approved_at)} />
                      </>
                    )}
                    {(Number(d.approved) === 2 || Number(d.approved) === 0) && (
                      <>
                        <InfoRow label="Disapproved By" value={fmt(d.disapproval_comment?.[0]?.disapprover_name)} />
                        <InfoRow label="Disapproved Date" value={fmt(d.disapproval_comment?.[0]?.disapproved_at)} />
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Attached File Section */}
              <div className={classes.reasonBox}>
                <div className={classes.reasonBoxHeader}>
                  <Icon.Document /> Attached File
                </div>
                <div className={classes.fileRow}>
                  {d.file ? (
                    <>
                      <span className={classes.fileName}>{d.file.split("/").pop()}</span>
                      <button className={classes.viewBtn} onClick={() => openFile(d.file)}>
                        View File
                      </button>
                    </>
                  ) : (
                    <span>No attachment uploaded.</span>
                  )}
                </div>
              </div>

              {/* Reason Box */}
              <div className={classes.reasonBox}>
                <div className={classes.reasonBoxHeader}>
                  <Icon.Note /> Reason (if any)
                </div>
                <div className={classes.reasonBoxText}>
                  {(() => {
                    const status = Number(d.approved);
                    if (status === 0) return "No comments to display as there is no action carried out on this loan.";
                    if (status === 1) {
                      const appComment = d.approval_comment?.[0]?.comment;
                      return appComment && appComment.trim() !== "" ? appComment : "No comments to display.";
                    }
                    if (status === 2) {
                      const disComment = d.disapproval_comment?.[0]?.comment;
                      return disComment && disComment.trim() !== "" ? disComment : "No comments to display.";
                    }
                    return "—";
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Modal Action Controls Sticky Footer */}
          {/* <div className={classes.modalActionFooter}>
          
          </div> */}

        </Modal.Body>
 

      {/* ── INTERNAL PROCESSING COMMENT MODAL LAYER ────────────────── */}
      <Modal
    show={showModal}
    onHide={() => setShowModal(false)}
    centered
    backdrop="static"
    keyboard={false}
>
    <Modal.Header closeButton>
        <Modal.Title>
             {approvalType === "additional"
        ? actionType === "approve"
            ? "Approve Additional Loan"
            : "Disapprove Additional Loan"
        : actionType === "approve"
            ? "Approve Loan"
            : "Disapprove Loan"}
        </Modal.Title>
    </Modal.Header>

    <Modal.Body>
        <p>Please enter your comment below.</p>

        <Form.Control
            as="textarea"
            rows={5}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter comment..."
            style={{width: '90%'}}
        />
    </Modal.Body>

    <Modal.Footer>
        <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
        >
            Cancel
        </Button>

        <Button
            variant={actionType === "approve" ? "success" : "danger"}
            onClick={handleAction}
            disabled={submitting}
        >
            {submitting
                ? "Processing..."
                : actionType === "approve"
                ? "Approve Loan"
                : "Disapprove Loan"}
        </Button>
    </Modal.Footer>
</Modal>

      {/* ── INTERNAL ATTACHMENT VIEW PREVIEW LAYER ────────────────── */}
      {showFileModal && (
        <div className={classes.innerModalOverlay}>
          <div className={classes.fileModal}>
            <div className={classes.fileHeader}>
              <h3>Loan Attachment</h3>
              <button className={classes.closeBtn} onClick={() => setShowFileModal(false)}>✕</button>
            </div>
            {selectedFile?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
              <img src={selectedFile} alt="Loan Attachment" className={classes.previewImage} />
            ) : selectedFile?.match(/\.pdf$/i) ? (
              <iframe title="Loan File" src={selectedFile} className={classes.previewPdf} />
            ) : (
              <div className={classes.downloadArea}>
                <p>Preview is unavailable for this file type.</p>
                <a href={selectedFile} target="_blank" rel="noreferrer" className={classes.downloadBtn}>
                  Open File
                </a>
              </div>
            )}
          </div>
        </div>
      )}
      <Modal.Footer>
        {Number(d.approved) === 0 && (
            <>
                <Button
                    variant="success"
                    onClick={() => openActionInputModal("approve")}
                    disabled={submitting}
                >
                    Approve
                </Button>

                <Button
                    variant="danger"
                    onClick={() => openActionInputModal("disapprove")}
                    disabled={submitting}
                >
                    Disapprove
                </Button>
            </>
        )}

        <Button
            variant="secondary"
            onClick={onClose}
        >
            Close
        </Button>
    </Modal.Footer>
</Modal>
    </>
  );
};

export default ApproveLoanModal;